/**
 * Copyright (c) 2026, CheckPlus Limited
 */

/**
 * Traditional Chinese (Hong Kong) copy for the Plug marketing site.
 *
 * Imported from the I18N table in `Plug Landing.dc.html` (Claude Design
 * project 3d0c2fbb), which now carries a proofread trilingual pass — see
 * `Plug Landing - Chinese Translation.md` in the same project for the
 * reviewer-facing table.
 *
 * Where copyEn.js carries a section 7b correction the design has not taken up
 * — the platform-fee wording, the three business rule types, and the figures
 * that would claim a launched product — the English has no counterpart in the
 * design's table, so the translation here follows copyEn.js rather than the
 * design. Correct all three languages together.
 *
 * Terminology follows the design's own glossary: 捐助規則 (giving rule),
 * 捐款計劃 (giving plan), 慈善義賣 (charity sale), 單位成本 (unit cost),
 * 非牟利機構 (nonprofit), 稅務條例第 88 條 (Section 88), 核數師 (auditor).
 */

const copyZh = {
  documentTitle: "Plug",

  nav: {
    about: "什麼是 Plug",
    donors: "捐款者",
    business: "商戶",
    nonprofit: "非牟利機構",
    login: "登入",
    logoAlt: "Plug",
    beta: "BETA",
    cta: {
      about: "註冊",
      donors: "註冊",
      business: "立即開始",
      nonprofit: "加入 Plug",
    },
  },

  about: {
    pill: "一個連繫商戶、非牟利機構與捐款者的公益平台",
    h1: "Plug：連繫社會各方的公益平台\n讓行善更簡單、更持續",
    lede:
      "Plug 將商戶、非牟利機構與捐款者連結於同一生態圈，令捐款更方便、更持續。商戶可以將行善與營業額掛鉤、融入日常營運；捐款者以最適合自己的方式支持所關心的慈善工作；" +
      "非牟利機構則獲得更多、更穩定的資助機會、提高曝光，並以更清晰的方式展示工作成效。",
    ctaPrimary: "瀏覽非牟利機構",
    ctaSecondary: "了解 Plug 如何連繫三方",
    flow: {
      kicker: "PLUG 如何連繫並支援三方",
      title: "一個互聯互通的捐款流程，為三方創造更多價值。",
      business: {
        name: "商戶",
        txt: "在 Plug 上輕鬆建立及管理不同類型的捐助計劃。",
      },
      donors: {
        name: "捐款者",
        txt: "輕鬆建立捐款計劃，支持不同非牟利機構，並可隨時調整。",
      },
      nonprofits: {
        name: "非牟利機構",
        txt: "透過 Plug 讓商戶及捐款者可更便利地捐款，獲得更多、更穩定的資助；同時向支持者展示捐款帶來的成果，吸引更多支持者。",
      },
      hub: "核實非牟利機構資格、簡化捐款流程、透過 Stripe 處理付款、推動慈善公益",
      bizOut: "捐出部分營業額、慈善義賣或更多",
      bizBack: "企業社會責任實證、自動化行政流程、提升顧客參與度",
      donorOut: "定期或單次捐款",
      donorBack: "自動化處理、稅務收據、非牟利機構最新動態",
      npDown: "獲得更多資助機會、簡化行政、提升機構知名度",
      npUp: "工作成果與公益成效更新",
    },
    sides: [
      {
        id: "business",
        kicker: "商戶得到什麼",
        title: "讓客戶看得見的企業社會責任。",
        txt:
          "在商戶結帳頁面及 Plug 商戶專頁上顯示你的捐助計劃（如捐出此訂單總額的 1%），讓顧客了解其消費所支持的公益項目，甚至與之互動。" +
          "Plug 協助商戶提升顧客參與度、購買意欲及品牌聲譽，同時處理捐款，並把企業社會責任紀錄集中管理。",
        link: "Plug 商戶方案 →",
      },
      {
        id: "donors",
        kicker: "捐款者得到什麼",
        title: "靈活捐款，所有紀錄集中管理。",
        txt:
          "建立一個或多個捐款計劃，自訂捐款金額及頻率，並將捐款分配予信任的非牟利機構。你可以隨時調整或暫停計劃、在帳戶內保存收據、接收慈善機構最新消息，" +
          "並支持回饋社會的商戶。",
        link: "Plug 捐款者方案 →",
      },
      {
        id: "nonprofit",
        kicker: "非牟利機構得到什麼",
        title: "更多資助，更少的行政工作。",
        txt:
          "商戶與捐款者均可透過 Plug 找到你的機構。Plug 讓與營業額掛鈎的商戶捐款及個人定期捐款更容易實行。你可透過商戶捐款規則、" +
          "捐款計劃及慈善義賣獲得支持，而你的 Plug 機構專頁與公益成效更新則有助吸引下一位支持者。",
        link: "Plug 非牟利機構方案 →",
      },
    ],
    valuesTitle: "Plug 的四大優勢",
    values: [
      {
        title: "簡單開始，輕鬆管理。",
        txt: "在 Plug 上輕鬆建立捐款規則及捐款計劃。付款處理、捐款分配及收據紀錄均可自動處理。",
      },
      {
        title: "為持續行善而設",
        txt: "Plug 透過顧客互動與公益成效實證，讓商戶的捐助帶來更高的企業社會責任價值，令他們有更充分的理由持續捐助。",
      },
      {
        title: "更公平的支援機會",
        txt: "不論規模大小，非牟利機構都能在 Plug 上被看見，並透過分享工作成效，幫助潛在捐款人了解和支持其工作。",
      },
      {
        title: "全程經過審核",
        txt: "非牟利機構上架前須被核實其第 88 條免稅資格；收款與付款均經 Stripe 處理，Plug 將不會持有任何款項。",
      },
    ],
  },

  donors: {
    pill: "人人都可以行善 · 0 間第 88 條認可非牟利機構",
    h1: "把每次消費的一小部分，化為善意。",
    lede: "設定一個金額，按你的心意分配給任意數量的香港非牟利機構，Plug 以單一付款完成轉帳，所有收據集中保存。每月最低 HK$100，亦可單次捐款。",
    ctaPrimary: "開始捐款",
    ctaSecondary: "瀏覽非牟利機構",
    note: "免費加入、沒有最低金額，任何月份都可更改或停止。付款成功後即可收到收據。",
    shot: "應用程式畫面 · 你的捐款計劃",
    press: {
      label: "媒體報導",
      logo: "標誌",
    },
    how: {
      kicker: "運作方式",
      h2: "三個決定，之後自動運作。",
      lede: "大部分人兩分鐘內完成設定便不用再理會，當然你亦可隨時調整分配。",
      steps: [
        {
          title: "支持你關心的公益範疇",
          txt: "按機構實際的服務範疇瀏覽——無家可歸、飢餓、殘疾、精神健康——再加入你信任的機構。",
          shot: "畫面 · 探索",
        },
        {
          title: "設定總額並分配",
          txt: "拖動任一比重，其餘會自動重新平衡，令分配總和永遠是 100%。只支持一間機構？那就完全不用設定百分比。",
          shot: "畫面 · 分配",
        },
        {
          title: "即時取得收據",
          txt: "付款成功即發出第 88 條扣稅收據，通常只需數秒，並附上成效報告，說明你的捐款換來了什麼。",
          shot: "畫面 · 成效",
        },
      ],
    },
    calc: {
      kicker: "立即試算 — 無須開立帳戶",
      h2: "你的捐款可以換來什麼？",
      lede: "以 Plug 上三間非牟利機構公布的真實單位成本計算，並按大部分新捐款者的分配方式劃分。",
      label: "我可以捐出",
      currency: "HK$",
      per: "/ 每月",
      footBefore: "即全年",
      footAfter: "，分配給三間非牟利機構。",
      yearly: "／年",
      photo: "相片預留位置",
    },
    onePayment: {
      kicker: "一次付款，一站管理",
      h2: "一次過支持多個公益項目，所有收據集中保存。",
      lede: "沒有人想為每間支持的機構分別設立自動轉帳、帳戶登入和 PDF 檔案。Plug 把這一切整合為單一付款與單一紀錄。",
      give: {
        title: "支持多間，只付一次",
        txt: "在同一計劃中加入任意數量的非牟利機構。總額以單一付款收取，再按你設定的比重分配——不用逐筆交易，也不用重複輸入信用卡資料。",
        oneLabel: "單一付款",
        oneValue: "HK$400 / 每月",
        checks: [
          "比重總和永遠為 100%——調整其一，其餘自動平衡",
          "增加或移除機構，總額維持不變",
        ],
      },
      receipts: {
        title: "所有收據，一處存放",
        txt: "每次成功付款都會產生一張涵蓋所有受助機構的第 88 條收據，付款完成即存入你的帳戶。報稅時只需下載整年紀錄，不用翻查一堆電郵。",
        label: "你的收據 · 2026",
        note: "符合第 88 條扣稅資格",
        pdf: "PDF",
        totalLabel: "年初至今",
        totalValue: "HK$3,200",
        rows: [
          {
            date: "2026 年 8 月 1 日",
            detail: "RC-2608 · 4 間非牟利機構",
            amount: "HK$400",
          },
          {
            date: "2026 年 7 月 1 日",
            detail: "RC-2607 · 4 間非牟利機構",
            amount: "HK$400",
          },
          {
            date: "2026 年 6 月 12 日",
            detail: "RC-2606b · 單次捐款，1 間非牟利機構",
            amount: "HK$1,000",
          },
          {
            date: "2026 年 6 月 1 日",
            detail: "RC-2606 · 3 間非牟利機構",
            amount: "HK$400",
          },
        ],
        checks: [
          "付款成功即發出，通常只需數秒",
          "一個檔案下載全年紀錄，方便報稅",
        ],
      },
    },
    marketplace: {
      kicker: "公益市集",
      h2: "0 間非牟利機構，按服務範疇分類。",
      lede: "每間機構均按稅務局第 88 條名單核實，並在檔案內公開經審核帳目及單位成本。",
      browse: "瀏覽所有非牟利機構",
      countSuffix: "間機構",
    },
    impact: {
      kicker: "本年度成效",
      h2: "日常消費，化成宿位、餐膳與照顧。",
      lede: "由各非牟利機構自行呈報，並按其檔案上公布的單位成本計算。",
      stats: [
        {
          num: "21,400",
          txt: "個深水埗及油麻地宿位晚上",
        },
        {
          num: "468,000",
          txt: "份剩食回收並送到家庭手上",
        },
        {
          num: "3,100",
          txt: "小時資助輔導，輪候不超過兩星期",
        },
        {
          num: "640",
          txt: "名殘疾成人成功投入公開就業",
        },
      ],
      quote: {
        txt: "「我設定每月 HK$200 分給三間機構，之後就忘記了。十一個月後，有一頁告訴我，這些捐款換來了八個晚上的宿位和一百六十份餐膳。」",
        mono: "TL",
        name: "Tania L.",
        meta: "自 2025 年 10 月起捐款 · 堅尼地城",
        photo: "相片",
      },
    },
    trust: [
      {
        kicker: "收費說明",
        title: "免費加入，免費上架。",
        txt: "費用只會從實際到帳的捐款中扣除——不會因為你在這裡而收取月費。",
      },
      {
        kicker: "核實而非隨意上架",
        title: "已向稅務局核實第 88 條資格",
        txt: "機構須先核實慈善團體編號、經審核帳目及服務成本，才可收取任何款項。",
      },
      {
        kicker: "資金流向",
        title: "由 Stripe 結算",
        txt: "款項從不停留在我們手上。付款成功即發出收據，撥款會合併為一筆清晰的轉帳交予機構。",
      },
    ],
    cross: [
      {
        id: "business",
        kicker: "你是商戶？",
        title: "自動捐出部分收入。",
        txt: "公布捐助規則、在店面展示，並自動取得企業社會責任報告。",
        link: "了解 Plug 商戶方案 →",
      },
      {
        id: "nonprofit",
        kicker: "你是非牟利機構？",
        title: "穩定的定期支持，帳目清晰完整。",
        txt: "以第 88 條編號登記上架，清楚看見哪些個人與商戶正在支持你。",
        link: "了解 Plug 非牟利機構方案 →",
      },
    ],
  },

  business: {
    pill: "0 間香港商戶正在 Plug 上行善",
    h1: "訂立一條捐助規則，其餘交給你的營業額。",
    lede:
      "可承諾捐出部分收入、每筆銷售的湊整捐款，或固定金額。Plug 會讀取你的 Stripe 結算數據、遵守你設定的上限、按你選擇的機構分配款項，" +
      "並在每次成功付款後發出收據。",
    ctaPrimary: "發布捐助規則",
    ctaSecondary: "聯絡我們團隊",
    note: "發布規則不收費用。規則公開前，我們會先核實你的商業登記。",
    shot: "管理介面 · 捐助規則",
    rules: {
      kicker: "選擇你的規則",
      h2: "三種承諾方式，一套報告系統。",
      lede: "帳目有變時可隨時轉換，捐助頻率亦可另行選擇。",
      cards: [
        {
          tag: "最常用",
          headline: "1–3%",
          title: "每月收入的一定比例",
          txt: "按已結算收入計算，生意好時多捐一點，淡季時自動減少。",
          note: "須連接 Shopify 商店。設定上限後，捐款金額不會超出。",
        },
        {
          tag: "面向顧客",
          headline: "0.5–5%",
          title: "每筆銷售的湊整捐款",
          txt: "每筆銷售湊整至最接近的一元，差額即為捐款，讓顧客看見自己的消費帶來了什麼——費用由你承擔，不會加到顧客身上。",
          note: "須連接 Shopify 商店。絕不向顧客額外收費。",
        },
        {
          tag: "可預算",
          headline: "固定",
          title: "固定金額",
          txt: "一個議定金額，不受營業額影響——最方便財務部門規劃。多久捐一次由你決定。",
          note: "可以每月捐助，亦可為單一活動作一次性捐助。",
        },
      ],
    },
    customers: {
      kicker: "顧客會看見什麼",
      h2: "看得見的善行，而不只是口號。",
      lede:
        "你的規則會以標章形式出現在店面、以附註出現在收據上，並設有公開頁面列出你資助的機構及已送達的金額。捐款由你支付——顧客只會知道自己的消費帶來了什麼，" +
        "不會被收取任何費用。",
      checks: [
        "店面標章及收據附註，提供三種語言",
        "讓員工每季提名一間機構加入分配名單",
        "核數師可直接採用的企業社會責任報告",
      ],
      badgeTitle: "店面標章",
      badgeTxt: "「我們把每筆銷售的 2% 捐給 3 間香港非牟利機構」",
      receiptLabel: "收據附註",
      subtotalLabel: "小計",
      subtotalValue: "HK$248.00",
      totalLabel: "應付總額",
      totalValue: "HK$248.00",
      receiptFootBefore: "多謝支持——這次購物代表",
      receiptFootWe: "我們",
      receiptFootMid: "會捐出 ",
      receiptFootAmount: "HK$4.96",
      receiptFootAfter: " 給 3 間香港非牟利機構，你的帳單並無額外收費。",
      photo: "相片 · 店面與標章",
    },
    directory: {
      kicker: "同行夥伴",
      h2: "已經透過 Plug 行善的商戶。",
      link: "查看完整名單 →",
      rows: [
        {
          name: "龍昌餅店",
          industry: "餐飲",
          district: "深水埗",
          rule: "收入的 2%",
        },
        {
          name: "港島牙科診所",
          industry: "醫療健康",
          district: "中環",
          rule: "每月 HK$10,000",
        },
        {
          name: "觀塘印刷公司",
          industry: "服務業",
          district: "觀塘",
          rule: "收入的 2.5%",
        },
        {
          name: "嘉道理咖啡烘焙",
          industry: "餐飲",
          district: "油麻地",
          rule: "每筆銷售的 1.5%",
        },
      ],
    },
    quote: {
      txt: "「以前我們只在聖誕節挑一間慈善機構捐款，其餘時間就忘記了。現在每月自動捐出收入的 2%，報告亦自動生成——顧客結帳時還會主動問起。」",
      mono: "LC",
      name: "店主",
      meta: "龍昌餅店 · 深水埗 · 自 2021 年起捐助",
      photo: "相片",
    },
  },

  nonprofit: {
    pill: "只限第 88 條註冊慈善團體",
    h1: "穩定的定期支持，帳目清晰完整。",
    ledeParts: [
      "登記上架後，你的機構便會成為眾多透過 Plug 捐款的個人及商戶的選項之一。款項以單一清晰轉帳到帳，並附逐項列明的結算表——流程無縫又方便。",
      "我們希望協助非牟利機構籌得更多資源，因此已認證的非牟利機構可完全免費在 Plug 上架！",
    ],
    ctaPrimary: "加入 Plug",
    ctaSecondary: "下載簡介單張",
    note: "免費上架，亦無月費。結算表會並列顯示捐款總額與實收金額。",
    shot: "管理介面 · 資金來源",
    why: {
      kicker: "為何選擇 Plug",
      h2: "更多方法，資助你的使命。",
      cards: [
        {
          id: "storefront",
          title: "來自商戶的無縫捐助",
          txt: "店舖與公司透過 Plug 承諾捐出部分收入。他們生意愈好，你的資金亦愈多，無須額外開口募捐。",
        },
        {
          id: "heart",
          title: "來自個人捐款者的定期捐款",
          txt: "捐款者只需設定一次金額，捐款便會持續，直至他們更改為止；不需每次都籌辦新活動。",
        },
        {
          id: "people",
          title: "提升慈善合作的曝光機會",
          txt: "你的使命頁面、最新動態與單位成本集中一處，方便商戶與捐款者找到你並展開合作。",
        },
        {
          id: "coin",
          title: "無上架費，無月費",
          txt: "上架及營運頁面完全免費。Plug 只就你實際收到的捐款收取平台費用。",
        },
      ],
    },
    dashboard: {
      kicker: "你的管理介面",
      h2: "清楚看見誰在支持你，以及支持的方式。",
      lede: "定期與單次、個人與商戶、每月各項捐助規則帶來多少款項，以及哪些支持者已停止捐款——讓你在預算受影響前先知道。",
      checks: [
        "單一 Stripe 轉帳，附上核數師可直接採用的結算表",
        "公布單位成本，讓捐款者知道 HK$120 實際換來什麼",
        "發布服務動態，並顯示在每位支持者的成效報告中",
      ],
      panelLabel: "本月收到",
      panelValue: "HK$142,800",
      individuals: {
        name: "個人",
        meta: "3,240 位把部分消費化為捐款的人",
        pct: "62%",
      },
      businesses: {
        name: "商戶",
        meta: "84 間已公布捐助規則",
        pct: "38%",
      },
      stats: [
        {
          num: "+186",
          txt: "本月新增支持者",
        },
        {
          num: "HK$96,400",
          txt: "屬定期捐款，收入可預算",
        },
      ],
    },
    checkplus: {
      pill: "深度整合 CHECKPLUS",
      h2: "連接 CheckPlus，後台工作自動運作。",
      lede:
        "Plug 由 CheckPlus 團隊開發，兩者完全打通。連接帳戶後，Plug 會直接沿用你現有的收據範本、報告、捐款頁面及籌款活動，" +
        "無須匯出檔案或重新輸入資料。",
      link: "了解 CheckPlus 整合 →",
      features: [
        {
          title: "自動收據",
          txt: "以你自訂的範本發出第 88 條收據，每次成功付款自動寄出，不用人手按發送。",
        },
        {
          title: "會計報告",
          txt: "可直接入帳的報告，格式符合核數師要求，年結時無須額外處理。",
        },
        {
          title: "完整付款資料",
          txt: "每筆交易均列明支持者、付款方式、分配比例及結算編號，一筆轉帳可追溯至每筆捐款。",
        },
        {
          title: "專屬捐款頁面",
          txt: "市集以外的品牌捐款頁面，可用連結或二維碼分享，收據自動發出。",
        },
        {
          title: "有目標的籌款活動",
          txt: "設定目標與限期，追蹤進度條；分開統計，一併撥款。",
        },
        {
          title: "即時支持者紀錄",
          txt: "捐款者自動成為聯絡人，定期計劃自動成為承諾捐款，資料自動更新。",
        },
        {
          title: "一組帳戶登入兩個平台",
          txt: "以 Plug 帳戶登入 CheckPlus，同一帳戶、同一團隊，不用第二個密碼。",
        },
      ],
      stripName: "CheckPlus",
      stripTxt:
        "一組帳戶登入兩個平台。你的 Plug 帳戶可直接登入 CheckPlus，不用另外開戶或記多一組密碼。還未使用 CheckPlus？我們會在你上架時代為設定，" +
        "因為 Stripe 驗證需經該平台完成。",
      stripCta: "連接帳戶",
    },
    join: {
      kicker: "如何加入",
      h2: "約一星期完成上架。",
      lede: "這一星期主要是我們核對文件，而不是你填表格。",
      steps: [
        {
          n: "1",
          title: "以第 88 條編號申請",
          txt: "提供慈善團體編號、最近期經審核帳目，以及以機構名義開立的銀行帳戶，填表只需十分鐘。",
        },
        {
          n: "2",
          title: "經 CheckPlus 完成 Stripe 驗證",
          txt: "我們會按稅務局名單核實你的編號，Stripe 驗證會在同一流程中透過 CheckPlus 完成，無論你是否新用戶。",
        },
        {
          n: "3",
          title: "公布你的單位成本",
          txt: "讓捐款者知道 HK$120 或 HK$800 實際換來什麼，這些數字會出現在每份成效報告中。",
        },
      ],
      eligibility: "資格：你的機構必須是在香港註冊，並根據《稅務條例》第 88 條獲豁免繳稅的慈善團體。我們會按稅務局名單核實編號，並查核你最近期的經審核帳目。",
      eligibilityCta: "查看申請資格",
    },
    quote: {
      txt: "「Plug 取代了我們整套捐款行政流程。八十四間商戶和三千位個人捐款者的款項化為一筆轉帳，結算表更可讓核數師直接採用。」",
      mono: "HS",
      name: "財務主管",
      meta: "港灣庇護信託 · 深水埗",
      photo: "相片",
    },
  },

  causes: {
    homeless: {
      label: "無家可歸",
      blurb: "宿位、外展服務及個案跟進，協助入住穩定居所。",
    },
    poverty: {
      label: "貧窮與飢餓",
      blurb: "剩食回收、社區飯堂及低收入家庭現金援助。",
    },
    disability: {
      label: "殘疾支援",
      blurb: "自立生活、就業指導及傳譯服務，有需要時免費提供。",
    },
    youth: {
      label: "青年與教育",
      blurb: "自修空間、補習支援及畢業生有薪學徒計劃。",
    },
    mental: {
      label: "精神健康",
      blurb: "按能力收費的心理治療及四種語言的 24 小時危機支援熱線。",
    },
    elderly: {
      label: "長者服務",
      blurb: "家訪、熱食送遞及獨居長者陪伴。",
    },
    environment: {
      label: "環境保護",
      blurb: "海岸修復、減廢及城市生物多樣性。",
    },
    animals: {
      label: "動物福利",
      blurb: "被遺棄動物的拯救、獸醫護理及領養安置。",
    },
  },

  outcomes: {
    shelter: {
      name: "港灣庇護信託",
      one: "個安全宿位晚上",
      many: "個安全宿位晚上",
    },
    food: {
      name: "九龍剩食回收",
      one: "份剩食送遞餐膳",
      many: "份剩食送遞餐膳",
    },
    counselling: {
      name: "靜港輔導中心",
      one: "小時資助輔導",
      many: "小時資助輔導",
    },
    brightSteps: {
      name: "步步向前網絡",
    },
  },

  faqs: {
    donors: [
      {
        q: "非牟利機構需要付上架費或訂閱費以使用 Plug 嗎？",
        a: "不需要。只要非牟利機構具備第 88 條認證，即可免費在 Plug 上架並使用其服務。",
      },
      {
        q: "我可以同時支持多間非牟利機構嗎？",
        a: "當然可以。只要設定一個捐款總額，加入你想支持的機構，並調整資金對每間機構的支持比例即可。若只支持一間機構，則完全不需設定百分比。",
      },
      {
        q: "我何時會收到收據？",
        a: "付款成功後即可收到。每張收據均符合第 88 條扣稅資格，並涵蓋該次付款的所有受助機構；即使分配給四間機構，也只會收到一份文件以方便捐款者。",
      },
      {
        q: "我可以只作單次捐款而非每月捐款嗎？",
        a: "可以。你可選擇單次捐款，收據將即時發出。而定期捐款可選每月、每季或每年一次。",
      },
      {
        q: "我可以更改或停止捐款嗎？",
        a: "無論捐款者或商戶，都可以在你的 Plug 帳戶內隨時更改捐款金額、調整比例、增減機構或暫停定期捐款計劃。",
      },
    ],
    business: [
      {
        q: "我們的捐助規則如何計算？",
        a:
          "以你的 Stripe 結算數據計算。你可選擇收入的一定比例、每筆銷售的湊整捐款，或固定金額，再選擇捐助頻率，並可自行設定上限；計算與存檔由 " +
          "Plug 處理。",
      },
      {
        q: "我們需要付出什麼成本？",
        a: "發布規則不收費用，亦無月費。你支付的只是規則所承諾的金額，加上每筆捐款的付款手續費。",
      },
      {
        q: "我們日後可以更改規則嗎？",
        a: "可以。帳目有變時，你可隨時轉換規則類型、調整百分比、更改上限或重新分配機構比重，新規則於下個月生效。",
      },
      {
        q: "員工可以參與挑選受助機構嗎？",
        a: "開啟員工提名功能後，團隊可每季投票選出一間機構加入分配名單。該機構包含在同一總額內，你的承諾金額不變。",
      },
      {
        q: "企業社會責任報告方面我們可獲得什麼？",
        a: "每月結算表、每次成功付款的收據，以及可分享的成效報告，列出受助機構及款項換來的成果。",
      },
    ],
    nonprofit: [
      {
        q: "哪些機構符合上架資格？",
        a: "在香港註冊並根據《稅務條例》第 88 條獲豁免繳稅的慈善團體。我們會按稅務局名單核實編號，並查核你最近期的經審核帳目。",
      },
      {
        q: "上架需要多少費用？",
        a: "加入不收費用，亦無月費。信用卡手續費會由支付服務商在款項到帳前扣除；結算表會並列顯示捐款總額與實收金額。",
      },
      {
        q: "我們何時及如何收到款項？",
        a: "所有捐款會合併為一筆 Stripe 轉帳，並附逐項列明的結算表，你只需核對一筆入帳，而非數以千計的小額款項。",
      },
      {
        q: "上架是否必須使用 CheckPlus？",
        a:
          "不需要。由於 Stripe 驗證需經 CheckPlus 完成，我們會在你上架時代為開設帳戶；你的 Plug 登入資料同樣適用，" +
          "不用管理第二組密碼。收據、報告、捐款頁面及籌款活動則可隨時啟用。",
      },
      {
        q: "我們可以看到支持者是誰嗎？",
        a: "商戶一律顯示，並列明其捐助規則。個人捐款者若同意公開便會顯示姓名，否則以匿名顯示；無論如何，金額與日期都會逐項列出。",
      },
      {
        q: "什麼是單位成本？是否必須公布？",
        a: "單位成本是以簡單語言說明你的服務價格——HK$120 或 HK$800 可以換來什麼。這是必填項目，因為它能把捐款金額轉化為捐款者信得過的一句說明。",
      },
    ],
  },

  sectionLabels: {
    flow: "如何連繫三方",
    sides: "三方各自得到什麼",
    values: "為何選擇 Plug",
    how: "運作方式",
    calculator: "你的捐款可以換來什麼",
    causes: "公益市集",
    impact: "公益成效",
    rules: "選擇你的規則",
    customers: "顧客會看見什麼",
    directory: "同行夥伴",
    why: "為何在 Plug 上架",
    dashboard: "你的管理介面",
    checkplus: "CheckPlus 整合",
    join: "如何加入",
    faq: "常見問題",
  },

  faq: {
    title: "常見問題",
    lede: "若有其他疑問，歡迎聯絡 Plug 團隊。",
    contact: "聯絡客戶支援 →",
  },

  cta: {
    about: {
      headline: "無論你是哪一方，一切由建立賬戶開始。",
      sub: "商戶、非牟利機構與捐款者都請在此處建立帳戶，加入 Plug 生態圈，一同為社會公益出一分力！",
      primary: "免費建立帳戶",
      secondary: "瀏覽非牟利機構",
    },
    donors: {
      headline: "由每月 HK$100 開始，首張收據當日送達。",
      sub: "個人、商戶與非牟利機構都在同一處註冊——我們只會問你一次身分。",
      primary: "免費建立帳戶",
      secondary: "瀏覽非牟利機構",
    },
    business: {
      headline: "這星期發布規則，全年自動報告。",
      sub: "我們核實你的商業登記，你選擇受助機構，捐助便會由下一個週期起按 Stripe 結算數據自動運作。",
      primary: "發布捐助規則",
      secondary: "聯絡我們團隊",
    },
    nonprofit: {
      headline: "登記上架，不用再四處追捐款。",
      sub: "免費上架、無月費，每月一筆轉帳，結算表可讓核數師直接採用。",
      primary: "加入 Plug",
      secondary: "查看申請資格",
    },
  },

  footer: {
    blurb: "屬於香港的公益平台。",
    onThisPageHead: "本頁內容",
    whoHead: "適合對象",
    whoAbout: "什麼是 Plug",
    whoDonors: "捐款者",
    whoBusiness: "商戶",
    whoNonprofit: "非牟利機構",
    whoReports: "公益成效報告",
    trustHead: "信任與保障",
    trustSecurity: "資訊保安",
    trustStripe: "Stripe 合作夥伴",
    trustSection88: "稅務條例第 88 條認證",
    trustPrivacy: "私隱政策",
    companyHead: "關於我們",
    companyAbout: "公司簡介",
    companyContact: "聯絡我們",
    companySupport: "支持香港",
    companyTerms: "服務條款",
    note: "© 2026 Plug。非牟利機構須為香港註冊並符合《稅務條例》第 88 條的免稅慈善團體，方可接收款項。",
    region: "香港 · 港幣",
  },
};

export default copyZh;
